import React, { useState, useEffect } from 'react';
import { getAuthHeaders } from "../../../helpers/api_helper";
import UiContent from "../../../Components/Common/UiContent";
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import { getFirebaseBackend } from "../../../helpers/firebase_helper";
import config from '../../../config';
import { 
    Card, 
    CardBody, 
    Col, 
    Container, 
    Form, 
    Input, 
    Label, 
    Row, 
    Button,
    Alert,
    FormFeedback,
    InputGroup,
    InputGroupText
} from 'reactstrap';
import PreviewCardHeader from '../../../Components/Common/PreviewCardHeader';
import Select from 'react-select';
import Flatpickr from "react-flatpickr";

const ManualPredictionEntry = () => {
    document.title = "Manual Prediction Entry | AI Financial Prediction Tracker";

    // Form state
    const [formData, setFormData] = useState({
        symbol: '',
        model: '',
        confidence: '',
        range_min: '',
        range_max: '',
        reason: '',
        prediction_window_days: 7,
        current_price: '',
        prediction_type: 'MANUAL',
        source_url: '',
        is_private: false
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);
    const [assets, setAssets] = useState([]);
    const [currentPrices, setCurrentPrices] = useState({});
    const [loadingPrice, setLoadingPrice] = useState(false);

    // Prediction window options
    const windowOptions = [
        { value: 7, label: '7 Days' },
        { value: 30, label: '30 Days' },
        { value: 90, label: '90 Days' },
        { value: 180, label: '180 Days' }
    ];

    // Prediction type options
    const typeOptions = [
        { value: 'AI_WITH_TA', label: 'AI with Technical Analysis' },
        { value: 'AI_WITHOUT_TA', label: 'AI without Technical Analysis' },
        { value: 'MANUAL', label: 'Manual Prediction' },
        { value: 'YOUTUBE', label: 'YouTube Analysis' },
        { value: 'TWITTER', label: 'Twitter/X Post' },
        { value: 'HUMAN', label: 'Human Expert' }
    ];

    // Mock current prices (will be replaced with API call)
    const mockPrices = {
        'BTC': 45250.75,
        'ETH': 2890.32,
        'AAPL': 185.67,
        'GOOGL': 142.85,
        'TSLA': 248.92,
        'MSFT': 378.45,
        'NVDA': 875.23
    };

    useEffect(() => {
        // Load initial prices for all symbols when component mounts
        const loadInitialPrices = async () => {
            if (assets.length === 0) {
                console.warn('⚠️ No assets available to load prices.');
                return;
            }

            const symbols = assets.map(asset => asset.value);
            try {
                console.log('📈 Loading initial prices using dedicated manual prediction batch endpoint...');
                const response = await fetch(`${config.api.API_URL}/api/manual-prediction/current-prices/batch`, {
                    method: 'POST',
                    headers: { ...getAuthHeaders(), 
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ symbols })
                });
                
                if (response.ok) {
                    const result = await response.json();
                    if (result.success && result.data && result.data.prices) {
                        const priceMap = {};
                        Object.values(result.data.prices).forEach(priceData => {
                            priceMap[priceData.symbol] = priceData.price;
                        });
                        setCurrentPrices(priceMap);
                        console.log('📈 Initial prices loaded:', priceMap);
                    } else {
                        console.error('❌ Invalid batch price response format:', result);
                    }
                } else {
                    console.error('❌ Failed to load initial prices');
                }
            } catch (error) {
                console.error('❌ Error loading initial prices:', error);
            }
        };

        if (assets.length > 0) {
            loadInitialPrices();
        }
    }, [assets]);

    // Function to fetch current price for a specific symbol
    const fetchCurrentPrice = async (symbol) => {
        if (!symbol) return;
        
        setLoadingPrice(true);
        try {
            console.log(`💰 Fetching price for ${symbol} using dedicated manual prediction endpoint...`);
            const response = await fetch(`${config.api.API_URL}/api/manual-prediction/current-price/${symbol}`, { headers: getAuthHeaders() });
            
            if (response.ok) {
                const result = await response.json();
                if (result.success && result.data) {
                    const data = result.data;
                    console.log(`✅ Price fetched for ${symbol}: $${data.price} (${data.source})`);
                    
                    // Update current prices cache
                    setCurrentPrices(prev => ({
                        ...prev,
                        [data.symbol]: data.price
                    }));
                    
                    // Update form data with the fetched price
                    setFormData(prev => ({
                        ...prev,
                        current_price: data.price.toString()
                    }));
                } else {
                    console.error(`❌ Invalid response format for ${symbol}:`, result);
                }
            } else {
                const errorData = await response.json();
                console.error(`❌ Error fetching price for ${symbol}:`, errorData.message);
                // Don't update price if API call fails
            }
        } catch (error) {
            console.error(`❌ Network error fetching price for ${symbol}:`, error);
        } finally {
            setLoadingPrice(false);
        }
    };

    // Handle input changes
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    // Handle select changes
    const handleSelectChange = (selectedOption, actionMeta) => {
        const { name } = actionMeta;
        setFormData(prev => ({
            ...prev,
            [name]: selectedOption?.value || ''
        }));

        // Auto-fetch current price for symbol selection
        if (name === 'symbol' && selectedOption) {
            fetchCurrentPrice(selectedOption.value);
        }
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};

        if (!formData.symbol) newErrors.symbol = 'Asset symbol is required';
        if (!formData.model) newErrors.model = 'Model/Predictor name is required';
        if (!formData.range_min) newErrors.range_min = 'Minimum price is required';
        if (!formData.range_max) newErrors.range_max = 'Maximum price is required';
        if (!formData.current_price) newErrors.current_price = 'Current price is required';
        if (!formData.reason) newErrors.reason = 'Prediction reasoning is required';

        // Validate price ranges
        if (formData.range_min && formData.range_max) {
            const min = parseFloat(formData.range_min);
            const max = parseFloat(formData.range_max);
            if (min >= max) {
                newErrors.range_max = 'Maximum price must be greater than minimum price';
            }
        }

        // Validate confidence
        if (formData.confidence && (parseFloat(formData.confidence) < 0 || parseFloat(formData.confidence) > 100)) {
            newErrors.confidence = 'Confidence must be between 0 and 100';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            // Get logged-in user
            const firebaseBackend = getFirebaseBackend();
            const user = firebaseBackend ? firebaseBackend.getAuthenticatedUser() : null;
            const createdBy = user?.email || user?.displayName || "unknown";

            // Prepare data for API call
            const apiData = {
                ...formData,
                range_min: parseFloat(formData.range_min),
                range_max: parseFloat(formData.range_max),
                current_price: parseFloat(formData.current_price),
                confidence: parseInt(formData.confidence),
                prediction_window_days: parseInt(formData.prediction_window_days),
                prediction_type: 'MANUAL',
                created_by: createdBy
            };

            // Call backend API
            const response = await fetch(`${config.api.API_URL}/api/predictions`, {
                method: 'POST',
                headers: { ...getAuthHeaders(), 
                    'Content-Type': 'application/json'
                },
                credentials: 'include', // Include session cookies
                body: JSON.stringify(apiData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to submit prediction');
            }

            const result = await response.json();
            setSubmitStatus({
                type: 'success',
                message: 'Prediction submitted successfully and saved to database!'
            });

            // Reset form
            setFormData({
                symbol: '',
                model: '',
                confidence: '',
                range_min: '',
                range_max: '',
                reason: '',
                prediction_window_days: 7,
                current_price: '',
                prediction_type: 'MANUAL',
                source_url: '',
                is_private: false
            });

        } catch (error) {
            setSubmitStatus({
                type: 'danger',
                message: 'Failed to submit prediction. Please try again.'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        // Fetch supported symbols from the backend
        const fetchSupportedSymbols = async () => {
            try {
                console.log('🔄 Fetching supported symbols from the backend...');
                const response = await fetch(`${config.api.API_URL}/api/ai-predictions/supported-assets`, { headers: getAuthHeaders() });

                if (response.ok) {
                    const result = await response.json();
                    if (result.success && result.data) {
                        const symbols = Object.keys(result.data.symbols).map(symbol => ({
                            value: symbol,
                            label: symbol
                        }));
                        setAssets(symbols);
                        console.log('✅ Supported symbols fetched:', symbols);
                        console.log('🔍 API response for supported symbols:', result);
                    } else {
                        console.error('❌ Invalid response format for supported symbols:', result);
                    }
                } else {
                    console.error('❌ Failed to fetch supported symbols');
                }
            } catch (error) {
                console.error('❌ Error fetching supported symbols:', error);
            }
        };

        fetchSupportedSymbols();
    }, []);

    useEffect(() => {
        // Debugging log to check assets state
        console.log('🔍 Current assets state:', assets);
    }, [assets]);

    return (
        <React.Fragment>
            <UiContent />
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Manual Prediction Entry" pageTitle="Predictions" />
                    
                    {submitStatus && (
                        <Alert color={submitStatus.type}>
                            {submitStatus.message}
                        </Alert>
                    )}

                    <Row>
                        <Col xxl={8} className="mx-auto">
                            <Card>
                                <PreviewCardHeader title="Enter New Prediction" />
                                <CardBody>
                                    <p className="text-muted mb-4">
                                        Submit a new financial prediction for tracking and performance analysis. 
                                        All fields marked with <span className="text-danger">*</span> are required.
                                    </p>

                                    <Form onSubmit={handleSubmit}>
                                        <Row>
                                            {/* Asset Symbol */}
                                            <Col md={6}>
                                                <div className="mb-3">
                                                    <Label className="form-label">
                                                        Asset Symbol <span className="text-danger">*</span>
                                                    </Label>
                                                    <Select
                                                        name="symbol"
                                                        options={assets}
                                                        placeholder="Select asset..."
                                                        onChange={handleSelectChange}
                                                        value={assets.find(option => option.value === formData.symbol)}
                                                        className={errors.symbol ? 'is-invalid' : ''}
                                                    />
                                                    {errors.symbol && <FormFeedback className="d-block">{errors.symbol}</FormFeedback>}
                                                </div>
                                            </Col>

                                            {/* Current Price */}
                                            <Col md={6}>
                                                <div className="mb-3">
                                                    <Label htmlFor="current_price" className="form-label">
                                                        Current Price <span className="text-danger">*</span>
                                                        {loadingPrice && (
                                                            <small className="text-muted ms-2">
                                                                <i className="ri-loader-2-line ri-spin me-1"></i>
                                                                Loading...
                                                            </small>
                                                        )}
                                                    </Label>
                                                    <InputGroup>
                                                        <InputGroupText>$</InputGroupText>
                                                        <Input
                                                            type="number"
                                                            step="0.01"
                                                            name="current_price"
                                                            id="current_price"
                                                            placeholder="Select a symbol to auto-fetch price"
                                                            value={formData.current_price}
                                                            onChange={handleInputChange}
                                                            invalid={!!errors.current_price}
                                                            disabled={loadingPrice}
                                                        />
                                                        {formData.symbol && (
                                                            <Button
                                                                color="outline-secondary"
                                                                type="button"
                                                                onClick={() => fetchCurrentPrice(formData.symbol)}
                                                                disabled={loadingPrice}
                                                                title="Refresh current price"
                                                            >
                                                                <i className={`ri-refresh-line ${loadingPrice ? 'ri-spin' : ''}`}></i>
                                                            </Button>
                                                        )}
                                                        <FormFeedback>{errors.current_price}</FormFeedback>
                                                    </InputGroup>
                                                    {formData.symbol && (
                                                        <div className="form-text">
                                                            <i className="ri-information-line me-1"></i>
                                                            Price auto-updates when you select a symbol. Click refresh to get the latest price.
                                                        </div>
                                                    )}
                                                </div>
                                            </Col>

                                            {/* Model/Predictor Name */}
                                            <Col md={6}>
                                                <div className="mb-3">
                                                    <Label htmlFor="model" className="form-label">
                                                        Model/Predictor Name <span className="text-danger">*</span>
                                                    </Label>
                                                    <Input
                                                        type="text"
                                                        name="model"
                                                        id="model"
                                                        placeholder="e.g., GPT-4, John Doe, TradingView User"
                                                        value={formData.model}
                                                        onChange={handleInputChange}
                                                        invalid={!!errors.model}
                                                    />
                                                    <FormFeedback>{errors.model}</FormFeedback>
                                                </div>
                                            </Col>

                                            {/* Prediction Type */}
                                            <Col md={6}>
                                                <div className="mb-3">
                                                    <Label className="form-label">Prediction Type</Label>
                                                    <Select
                                                        name="prediction_type"
                                                        options={typeOptions}
                                                        placeholder="Select type..."
                                                        onChange={handleSelectChange}
                                                        value={typeOptions.find(option => option.value === formData.prediction_type)}
                                                    />
                                                </div>
                                            </Col>

                                            {/* Price Range */}
                                            <Col md={6}>
                                                <div className="mb-3">
                                                    <Label htmlFor="range_min" className="form-label">
                                                        Minimum Price <span className="text-danger">*</span>
                                                    </Label>
                                                    <InputGroup>
                                                        <InputGroupText>$</InputGroupText>
                                                        <Input
                                                            type="number"
                                                            step="0.01"
                                                            name="range_min"
                                                            id="range_min"
                                                            placeholder="0.00"
                                                            value={formData.range_min}
                                                            onChange={handleInputChange}
                                                            invalid={!!errors.range_min}
                                                        />
                                                        <FormFeedback>{errors.range_min}</FormFeedback>
                                                    </InputGroup>
                                                </div>
                                            </Col>

                                            <Col md={6}>
                                                <div className="mb-3">
                                                    <Label htmlFor="range_max" className="form-label">
                                                        Maximum Price <span className="text-danger">*</span>
                                                    </Label>
                                                    <InputGroup>
                                                        <InputGroupText>$</InputGroupText>
                                                        <Input
                                                            type="number"
                                                            step="0.01"
                                                            name="range_max"
                                                            id="range_max"
                                                            placeholder="0.00"
                                                            value={formData.range_max}
                                                            onChange={handleInputChange}
                                                            invalid={!!errors.range_max}
                                                        />
                                                        <FormFeedback>{errors.range_max}</FormFeedback>
                                                    </InputGroup>
                                                </div>
                                            </Col>

                                            {/* Prediction Window */}
                                            <Col md={6}>
                                                <div className="mb-3">
                                                    <Label className="form-label">Prediction Window</Label>
                                                    <Select
                                                        name="prediction_window_days"
                                                        options={windowOptions}
                                                        placeholder="Select timeframe..."
                                                        onChange={handleSelectChange}
                                                        value={windowOptions.find(option => option.value === formData.prediction_window_days)}
                                                    />
                                                </div>
                                            </Col>

                                            {/* Confidence Level */}
                                            <Col md={6}>
                                                <div className="mb-3">
                                                    <Label htmlFor="confidence" className="form-label">
                                                        Confidence Level (%)
                                                    </Label>
                                                    <InputGroup>
                                                        <Input
                                                            type="number"
                                                            min="0"
                                                            max="100"
                                                            name="confidence"
                                                            id="confidence"
                                                            placeholder="85"
                                                            value={formData.confidence}
                                                            onChange={handleInputChange}
                                                            invalid={!!errors.confidence}
                                                        />
                                                        <InputGroupText>%</InputGroupText>
                                                        <FormFeedback>{errors.confidence}</FormFeedback>
                                                    </InputGroup>
                                                </div>
                                            </Col>

                                            {/* Source URL */}
                                            <Col md={12}>
                                                <div className="mb-3">
                                                    <Label htmlFor="source_url" className="form-label">
                                                        Source URL (Optional)
                                                    </Label>
                                                    <Input
                                                        type="url"
                                                        name="source_url"
                                                        id="source_url"
                                                        placeholder="https://youtube.com/watch?v=..."
                                                        value={formData.source_url}
                                                        onChange={handleInputChange}
                                                    />
                                                    <div className="form-text">
                                                        Link to YouTube video, Twitter post, or other source
                                                    </div>
                                                </div>
                                            </Col>

                                            {/* Prediction Reasoning */}
                                            <Col md={12}>
                                                <div className="mb-3">
                                                    <Label htmlFor="reason" className="form-label">
                                                        Prediction Reasoning <span className="text-danger">*</span>
                                                    </Label>
                                                    <Input
                                                        type="textarea"
                                                        rows="4"
                                                        name="reason"
                                                        id="reason"
                                                        placeholder="Explain the reasoning behind this prediction..."
                                                        value={formData.reason}
                                                        onChange={handleInputChange}
                                                        invalid={!!errors.reason}
                                                    />
                                                    <FormFeedback>{errors.reason}</FormFeedback>
                                                </div>
                                            </Col>

                                            {/* Privacy Setting */}
                                            <Col md={12}>
                                                <div className="mb-3">
                                                    <div className="form-check">
                                                        <Input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            name="is_private"
                                                            id="is_private"
                                                            checked={formData.is_private}
                                                            onChange={handleInputChange}
                                                        />
                                                        <Label className="form-check-label" htmlFor="is_private">
                                                            <i className="ri-lock-line me-1"></i>
                                                            Make this prediction private
                                                        </Label>
                                                    </div>
                                                    <div className="form-text">
                                                        Private predictions are only visible to you and won't appear in public lists
                                                    </div>
                                                </div>
                                            </Col>


                                            {/* Submit Button */}
                                            <Col md={12}>
                                                <div className="text-end">
                                                    <Button
                                                        type="submit"
                                                        color="primary"
                                                        size="lg"
                                                        disabled={isSubmitting}
                                                    >
                                                        {isSubmitting ? (
                                                            <>
                                                                <i className="mdi mdi-spin mdi-loading me-1"></i>
                                                                Submitting...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="ri-save-line me-1"></i>
                                                                Submit Prediction
                                                            </>
                                                        )}
                                                    </Button>
                                                </div>
                                            </Col>
                                        </Row>
                                    </Form>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default ManualPredictionEntry;
